import React, { useMemo, useRef, type ChangeEvent } from 'react';
import {
  Stack,
  Group,
  Text,
  Paper,
  Button,
  ActionIcon,
  Menu,
  ScrollArea,
  Box,
  Badge,
  Center,
  AspectRatio,
  Image,
  SimpleGrid,
  UnstyledButton,
  Divider,
} from '@mantine/core';
import {
  IconMessageCircle,
  IconUserPlus,
  IconSparkles,
  IconNotes,
  IconClipboardList,
  IconFlag,
  IconUserOff,
  IconDots,
} from '@tabler/icons-react';
import type { ContactDirectoryUser } from '@domain/contacts/types';
import { resolveMediaUrl, type MediaLocator } from '@domain/media/resolve';
import type { Article } from '@domain/plaza/types';
import { UserAvatar } from '@shared/ui/UserAvatar';
import { initials, formatListTime } from '@shared/utils/ui';
import { blockUser } from '@domain/safety/blacklist';
import { submitReport } from '@domain/safety/report';
import { RightDrawer } from '@shared/ui/RightDrawer';

type UserProfileModalProps = {
  opened: boolean;
  onClose: () => void;
  user: ContactDirectoryUser | null;
  homepageBackground?: MediaLocator | null;
  currentUser: { id: number; name?: string } | null;
  isFriend: boolean;
  isPending: boolean;
  plazaArticles: Article[];
  cdnBase: string;
  apiBase: string;
  onStartConversationDraft: (user: ContactDirectoryUser) => void;
  onAddFriend: (user: ContactDirectoryUser) => void;
  onOpenDirectChat: (user: ContactDirectoryUser) => void;
  setPreviewImageUrl: (url: string | null) => void;
  onPickHomepageBackground?: (file: File) => void;
};

export function UserProfileModal({
  opened,
  onClose,
  user,
  homepageBackground,
  currentUser,
  isFriend,
  isPending,
  plazaArticles,
  cdnBase,
  apiBase,
  onStartConversationDraft,
  onAddFriend,
  onOpenDirectChat,
  setPreviewImageUrl,
  onPickHomepageBackground,
}: UserProfileModalProps) {
  const homepageBackgroundInputRef = useRef<HTMLInputElement>(null);
  const isSelf = currentUser && user && currentUser.id === user.id;

  // Filter articles created by this user
  const userArticles = useMemo(() => {
    if (!user) return [];
    return plazaArticles.filter((article) => article.authorUserId === user.id);
  }, [user, plazaArticles]);

  // Keep the Drawer mounted while closed so Mantine can run its enter transition
  // when `opened` changes from false to true.
  if (!user) return null;

  const resolvedHomepageBackground = homepageBackground ?? user.homepageBackground;
  const homepageBackgroundUrl = resolvedHomepageBackground
    ? resolveMediaUrl(resolvedHomepageBackground, cdnBase)
    : '';

  return (
    <RightDrawer
      opened={opened}
      onClose={onClose}
    >
      <ScrollArea.Autosize
        className="user-profile-modal__scroll"
        type="auto"
        offsetScrollbars
        scrollbarSize={8}
      >
        <Stack gap="lg" p="lg">
          {/* Profile Card Banner */}
          <Paper
            p={{ base: 'md', sm: 'lg' }}
            radius="lg"
            className="user-profile-hero"
            style={{ cursor: isSelf ? 'pointer' : 'default' }}
            role={isSelf ? 'button' : undefined}
            tabIndex={isSelf ? 0 : undefined}
            aria-label={isSelf ? '更换个人主页背景' : undefined}
            onClick={isSelf ? () => homepageBackgroundInputRef.current?.click() : undefined}
            onKeyDown={isSelf ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                homepageBackgroundInputRef.current?.click();
              }
            } : undefined}
          >
            {homepageBackgroundUrl && (
              <>
                <Image
                  className="user-profile-hero__background"
                  src={homepageBackgroundUrl}
                  alt=""
                  aria-hidden="true"
                />
                <Box className="user-profile-hero__scrim" aria-hidden="true" />
              </>
            )}
            {!isSelf && user.id > 0 && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    className="user-profile-hero__menu"
                    variant="subtle"
                    color="gray"
                    radius="xl"
                    aria-label="更多操作"
                  >
                    <IconDots size={20} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<IconFlag size={16} />} onClick={() => {
                    const detail = window.prompt('请补充举报说明（可选）') || undefined;
                    void submitReport('user', String(user.id), 'other', detail, apiBase)
                      .then(() => window.alert('举报已提交，我们会尽快处理'))
                      .catch((error) => window.alert(`举报失败：${error.message}`));
                  }}>
                    举报用户
                  </Menu.Item>
                  <Menu.Item color="red" leftSection={<IconUserOff size={16} />} onClick={() => {
                    if (!window.confirm(`屏蔽 ${user.name}？屏蔽后将移除当前关系和单聊会话。`)) return;
                    void blockUser(user.id, 'Blocked from profile', apiBase)
                      .then(() => {
                        window.alert('已屏蔽该用户');
                        onClose();
                      })
                      .catch((error) => window.alert(`屏蔽失败：${error.message}`));
                  }}>
                    屏蔽用户
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
            <Group
              className={homepageBackgroundUrl ? 'user-profile-hero__content user-profile-hero__content--on-image' : 'user-profile-hero__content'}
              gap="lg"
              wrap="nowrap"
              align="center"
            >
              <UserAvatar
                className="user-profile-hero__avatar"
                id={user.id}
                name={user.name}
                size={80}
                avatarUrl={user.avatarUrl}
                cdnBase={cdnBase}
              />
              <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                <Text fw={850} size="xl" truncate>
                  {user.name || '平台用户'}
                </Text>
                {user.signInfo && <Text size="sm" c={homepageBackgroundUrl ? 'inherit' : 'dimmed'} lineClamp={2}>{user.signInfo}</Text>}
                {isSelf && (
                  <Badge size="xs" color="gray" variant="light" radius="xs" w="fit-content">
                    我自己
                  </Badge>
                )}
              </Stack>
            </Group>
            {isSelf && (
              <input
                ref={homepageBackgroundInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const file = event.currentTarget.files?.[0];
                  event.currentTarget.value = '';
                  if (file) onPickHomepageBackground?.(file);
                }}
              />
            )}
          </Paper>

          {/* Relationship Actions */}
          {!isSelf && user.id > 0 && (
            <Box>
              {isFriend ? (
                <Group className="user-profile-actions" gap="sm" wrap="wrap">
                  <Button
                    className="user-profile-action"
                    variant="default"
                    size="sm"
                    radius="xl"
                    leftSection={<IconMessageCircle size={18} />}
                    onClick={() => {
                      onClose();
                      onOpenDirectChat(user);
                    }}
                  >
                    发消息
                  </Button>
                  <Button
                    className="user-profile-action user-profile-action--primary"
                    size="sm"
                    radius="xl"
                    leftSection={<IconClipboardList size={18} />}
                    onClick={() => {
                      onClose();
                      onStartConversationDraft(user);
                    }}
                  >
                    发起会话
                  </Button>
                </Group>
              ) : isPending ? (
                <Button className="user-profile-action" size="sm" radius="xl" disabled color="gray" variant="light">
                  关系申请中...
                </Button>
              ) : (
                <Button
                  className="user-profile-action user-profile-action--primary"
                  fullWidth
                  size="sm"
                  radius="xl"
                  leftSection={<IconUserPlus size={18} />}
                  onClick={() => {
                    onClose();
                    onAddFriend(user);
                  }}
                >
                  添加好友后可以发起会话
                </Button>
              )}
            </Box>
          )}

          <Divider label={<Text size="xs" c="dimmed" fw={700}>动态记录 ({userArticles.length})</Text>} labelPosition="center" />

          {/* User's articles feed — the modal owns scrolling to avoid nested scroll areas. */}
          {userArticles.length === 0 ? (
            <Center py={56}>
              <Stack align="center" gap="xs">
                <IconSparkles size={28} color="var(--mantine-color-gray-4)" />
                <Text size="xs" c="dimmed" ta="center">
                  暂无动态
                </Text>
              </Stack>
            </Center>
          ) : (
            <Stack gap="sm" pr="xs">
              {userArticles.map((article) => {
                const images = article.media?.images ?? [];
                const coverCount = Math.min(images.length, 9);
                return (
                  <Paper key={article.id} withBorder p="sm" radius="md" bg="var(--mantine-color-body)">
                    <Group justify="space-between" mb="xs">
                      <Text size="xs" fw={800} truncate max-width="60%">
                        {article.title || '动态'}
                      </Text>
                      <Group gap={4}>
                        <Text size="10px" c="dimmed">
                          {formatListTime(article.created)}
                        </Text>
                      </Group>
                    </Group>

                    {article.body && (
                      <Text size="xs" lineClamp={3} mb={coverCount > 0 ? 'xs' : 0}>
                        {article.body.trim()}
                      </Text>
                    )}
                    <Button
                      mt="xs"
                      size="compact-xs"
                      variant="subtle"
                      color="orange"
                      leftSection={<IconFlag size={13} />}
                      onClick={() => {
                        const detail = window.prompt('请补充举报说明（可选）') || undefined;
                        void submitReport('article', String(article.id), 'other', detail, apiBase)
                          .then(() => window.alert('动态举报已提交'))
                          .catch((error) => window.alert(`举报失败：${error.message}`));
                      }}
                    >
                      举报动态
                    </Button>

                    {coverCount > 0 && (
                      <SimpleGrid cols={4} spacing="xs">
                        {images.slice(0, 4).map((payload, imgIdx) => {
                          const imageUrl = payload.url;
                          return (
                            <AspectRatio ratio={1} key={`${article.id}-${imgIdx}`}>
                              <UnstyledButton
                                onClick={() => setPreviewImageUrl(imageUrl)}
                                style={{
                                  overflow: 'hidden',
                                  borderRadius: 8,
                                  border: '1px solid var(--mantine-color-gray-2)',
                                }}
                              >
                                <Image src={imageUrl} alt="Article" fit="cover" height="100%" />
                              </UnstyledButton>
                            </AspectRatio>
                          );
                        })}
                      </SimpleGrid>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Stack>
      </ScrollArea.Autosize>
    </RightDrawer>
  );
}
